import { forwardRef } from 'react';
import type { PropsWithoutRef, ReactNode } from 'react';

import type { HeadingTag, OmitStrict, RefElement } from '../../types';
import { useTestIds, useUID } from '../../hooks';
import Text from '../Text';
import { withTestIds } from '../../utils';

import type { SelectionCardProps } from './SelectionCard.types';
import { getSelectionCardTestIds } from './SelectionCard.test-ids';
import SelectionCard from './SelectionCard';

export interface SelectionCardDisplayProps
  extends OmitStrict<SelectionCardProps, 'heading' | 'disabled'> {
  label: NonNullable<ReactNode>;
  /**
   * Uses specific heading tag for header.
   * @default h4
   */
  headingTag?: HeadingTag;
}

const SelectionCardDisplay = forwardRef<
  RefElement<SelectionCardDisplayProps>,
  PropsWithoutRef<SelectionCardDisplayProps>
>(function SelectionCardDisplay(props, ref) {
  const uid = useUID();
  const {
    testId,
    id = uid,
    'aria-label': ariaLabel,
    label,
    headingTag = 'h4',
    ...restProps
  } = props;
  const labelId = `${id}-label`;

  const testIds = useTestIds(testId, getSelectionCardTestIds);

  return (
    <SelectionCard
      {...restProps}
      testId={testIds}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabel ? undefined : labelId}
      heading={
        <Text variant={headingTag} as='span' id={labelId} data-testid={testIds.label}>
          {label}
        </Text>
      }
      ref={ref}
    />
  );
});

export default withTestIds(SelectionCardDisplay, getSelectionCardTestIds);
